import type { ServerEvent } from "@/context/server-sdk"
import type { Message, Part } from "@opencode-ai/sdk/v2/client"
import type { DatoxSession } from "./api"

export type WorkflowScope = { projectID: string; periodID?: string; caseID?: string }
export type WorkflowSessionStatus = "idle" | "busy" | "retry"

export function workflowStatusSnapshot(snapshot: Record<string, WorkflowSessionStatus>, revision: number, events: Record<string, { status: WorkflowSessionStatus; revision: number }>) {
  const statuses = { ...snapshot }
  for (const [id, event] of Object.entries(events)) {
    if (event.revision > revision) statuses[id] = event.status
  }
  return statuses
}

export function workflowChatIDs(sessions: DatoxSession[], scope: WorkflowScope) {
  return sessions.filter((session) =>
    session.project_id === scope.projectID &&
    (session.period_id ?? undefined) === scope.periodID &&
    (session.session_metadata?.workflow_case_id ?? session.session_metadata?.risk_case_id ?? undefined) === scope.caseID &&
    session.status !== "deleted",
  ).flatMap((session) => session.remote_opencode_session_id ? [session.remote_opencode_session_id] : [])
}

export function workflowStatusEvent(event: ServerEvent): { sessionID: string; status: WorkflowSessionStatus } | undefined {
  const current = event.current
  if (current?.type === "session.execution.started") return { sessionID: current.data.sessionID, status: "busy" }
  if (current?.type === "session.retry.scheduled") return { sessionID: current.data.sessionID, status: "retry" }
  if (current?.type === "session.execution.succeeded" || current?.type === "session.execution.failed" || current?.type === "session.execution.interrupted") {
    return { sessionID: current.data.sessionID, status: "idle" }
  }
  if (event.type === "session.status") return { sessionID: event.properties.sessionID, status: event.properties.status.type }
}

export function workflowProcessingStatus(input: {
  roots: string[]
  statuses: Record<string, WorkflowSessionStatus>
  parents: Record<string, { parentID?: string } | undefined>
  waiting: (sessionID: string) => boolean
  automationStatus?: string
}) {
  const statuses = workflowSessionIDs(input.roots, Object.keys(input.statuses), input.parents).map((id) =>
    input.waiting(id) ? "waiting" : input.statuses[id] ?? "idle")
  if (statuses.includes("busy") || input.automationStatus === "running" || input.automationStatus === "pending") return "processing"
  if (statuses.includes("retry")) return "retrying"
  if (statuses.includes("waiting")) return "waiting"
  if (input.automationStatus === "waiting_for_files") return "waitingForFiles"
  return "idle"
}

export function workflowSessionIDs(rootIDs: string[], candidates: string[], parents: Record<string, { parentID?: string } | undefined>) {
  const roots = new Set(rootIDs)
  return [...new Set([...rootIDs, ...candidates])].filter((id) => {
    const visited = new Set<string>()
    let current: string | undefined = id
    while (current && !roots.has(current) && !visited.has(current)) {
      visited.add(current)
      current = parents[current]?.parentID
    }
    return !!current && roots.has(current)
  })
}

const operationStages = {
  "get-context": "preparing",
  "list-files": "reviewing",
  "get-file": "reviewing",
  "get-file-content": "reviewing",
  "list-categories": "reviewing",
  "save-extraction": "savingExtraction",
  "list-transformations": "analysing",
  "save-transformation": "savingOutputs",
  "list-artifacts": "validating",
  "save-artifact": "savingReport",
  "list-dashboards": "buildingDashboard",
  "get-dashboard": "buildingDashboard",
  "list-dashboard-datasets": "buildingDashboard",
  "preview-dashboard-dataset": "buildingDashboard",
  "save-dashboard": "savingDashboard",
} as const

export function workflowToolStage(tool: string, input: Record<string, unknown>) {
  const name = tool.replaceAll("_", "-").toLowerCase()
  if (Object.hasOwn(operationStages, name)) return operationStages[name as keyof typeof operationStages]
  if (name === "run-generated-extractor") return "extracting"
  if (name === "execute-definition-fixture") return "validating"
  if (name === "skill" || name === "todowrite") return "preparing"
  if (["read", "glob", "grep", "list", "webfetch", "websearch"].includes(name)) return "reviewing"
  if (["write", "edit", "apply-patch"].includes(name)) return "preparingOutputs"
  if (name === "task") return "coordinating"
  if (!["bash", "shell", "python"].includes(name)) return undefined

  // Inspect only the executable operation, never prose, tool output or payload contents.
  const command = typeof input.command === "string" ? input.command.trim() : ""
  const invocation = command.match(/^(?:python(?:3(?:\.\d+)?)?|bun|node)\s+["']?([^\s"']+)["']?(?:\s+([a-z-]+))?/)
  const script = invocation?.[1].split("/").at(-1)
  const operation = invocation?.[2]
  if (script === "datox_agent_api.py" && operation && Object.hasOwn(operationStages, operation)) return operationStages[operation as keyof typeof operationStages]
  if (script === "render_factsheet.py") return "generatingReport"
  if (script === "run-generated-extractor.ts") return "extracting"
  if (script === "execute-definition-fixture.ts") return "validating"
  return "analysing"
}

export function workflowSessionActivity(messages: Message[], parts: Record<string, Part[]>) {
  const message = messages.at(-1)
  if (!message || message.role !== "assistant" || message.time.completed) return undefined
  const current = parts[message.id] ?? []
  const tools = current.flatMap((part) => {
    if (part.type !== "tool" || (part.state.status !== "running" && part.state.status !== "pending")) return []
    const stage = workflowToolStage(part.tool, part.state.input)
    if (!stage) return []
    return [{ stage, priority: stage === "coordinating" ? 1 : 2, time: part.state.status === "running" ? part.state.time.start : message.time.created }]
  }).sort((a, b) => b.priority - a.priority || b.time - a.time)
  if (tools.length) return tools[0]
  if (current.some((part) => part.type === "reasoning" && !part.time.end)) return { stage: "thinking" as const, priority: 0, time: message.time.created }
  const last = current.at(-1)
  if (last?.type === "text" && !last.synthetic && !last.time?.end) return { stage: "responding" as const, priority: 0, time: message.time.created }
}
